import { adapter } from '@seek/tealium-adapter';
import { useUserProfile } from 'src/components/Notifications/hooks/useUserProfile';
import { useTypedSelector } from 'src/reducers';
import {
  useGetApproachability,
  useGetProfileVisibility,
  useGetSharedProfileVisibility,
} from '../graphql/visibility/data';
import { useMelwaysInfo } from '@seek/melways-react';

const tealiumAdapter = adapter();

let candidateId: number;
let loginGuid: string;

const CURRENT_PAGE_SECTION = ['employer site', 'shared profile'] as const;

export type AnalyticsCurrentPageSection = (typeof CURRENT_PAGE_SECTION)[number];

const PROFILE_VISIBILITY_LEVEL = [
  'standard',
  'limited',
  'hidden',
  'not set',
] as const;
export type AnalyticsProfileVisibilityLevel =
  (typeof PROFILE_VISIBILITY_LEVEL)[number];

const PROFILE_APPROACHABILITY_STATUS = ['on', 'off'] as const;
export type AnalyticsProfileApproachabilityStatus =
  (typeof PROFILE_APPROACHABILITY_STATUS)[number];

const SHARED_PROFILE_STATUS = ['on', 'off', 'not generated'] as const;
export type AnalyticsSharedProfileStatus =
  (typeof SHARED_PROFILE_STATUS)[number];

export const getSharedProfileTrackingStatus = ({
  isShareableProfileCreated,
  isShareableProfileActive,
}: {
  isShareableProfileCreated: boolean;
  isShareableProfileActive: boolean;
}) => {
  if (!isShareableProfileCreated) {
    return 'not generated';
  }
  if (isShareableProfileActive) {
    return 'on';
  }
  return 'off';
};

let profileTrackingContext: ProfileTrackingContext;

interface ProfileTrackingContext {
  brand: string;
  siteCountry: string;
  zone: string;
  siteLanguage: string;
  profileVisibilityLevel: AnalyticsProfileVisibilityLevel;
  profileApproachabilityStatus: AnalyticsProfileApproachabilityStatus;
  profileSharedStatus: AnalyticsSharedProfileStatus;
}

const profileState = () => ({
  currentPage: 'visibility settings',
  loginId: candidateId,
  isLoggedIn: Boolean(candidateId),
  actionOrigin: 'settings',
  profileGuid: loginGuid,
  ...profileTrackingContext,
});

export const setCandidateId = (
  id: number,
  emailCorrelationId: string,
): void => {
  candidateId = id;
  loginGuid = emailCorrelationId;
};

export const useSetCandidateId = () => {
  const { user } = useUserProfile();

  if (user) {
    setCandidateId(user.id, user.emailCorrelationId);
  }
};

export const setProfileTrackingContext = (
  brand: string,
  country: string,
  zone: string,
  language: string,
  profileVisibilityLevel: AnalyticsProfileVisibilityLevel,
  profileApproachabilityStatus: AnalyticsProfileApproachabilityStatus,
  profileSharedStatus: AnalyticsSharedProfileStatus,
): void => {
  profileTrackingContext = {
    brand,
    siteCountry: country,
    zone,
    siteLanguage: language,
    profileVisibilityLevel,
    profileApproachabilityStatus,
    profileSharedStatus,
  };
};

export const useSetTrackingContext = () => {
  const brand = useTypedSelector((state) => state.page.pageAnalytics.brand);
  const country = useTypedSelector((state) => state.page.pageAnalytics.country);
  const zone = useTypedSelector((state) => state.page.pageAnalytics.zone);
  const { language } = useMelwaysInfo();
  const { profileVisibilityLevel } = useGetProfileVisibility();
  const { approachable } = useGetApproachability();
  const { isShareableProfileActive, shareableProfileUrl } =
    useGetSharedProfileVisibility();

  setProfileTrackingContext(
    brand,
    country,
    zone,
    language,
    profileVisibilityLevel
      ? (profileVisibilityLevel?.toLowerCase() as AnalyticsProfileVisibilityLevel)
      : 'not set',
    approachable ? 'on' : 'off',
    getSharedProfileTrackingStatus({
      isShareableProfileCreated: Boolean(shareableProfileUrl),
      isShareableProfileActive,
    }),
  );
};

export const trackProfileCardOpened = ({
  currentPageSubsection,
}: {
  currentPageSubsection: AnalyticsCurrentPageSection;
}) => {
  tealiumAdapter.link('profile_section_displayed', {
    ...profileState(),
    eventName: 'profile_section_displayed',
    currentPageSection: 'visibility settings',
    currentPageSectionState: 'edit',
    currentPageSubsection,
  });
};

export const trackProfileVisibilityUpdated = (
  profileVisibilityLevel: AnalyticsProfileVisibilityLevel,
) => {
  tealiumAdapter.link('profile_update_succeeded', {
    ...profileState(),
    eventName: 'profile_update_succeeded',
    currentPageSection: 'visibility settings',
    updateType: 'edit',
    currentPageSubsection: 'employer site',
    profileVisibilityLevel,
  });
};

export const trackApproachabilityUpdated = ({
  approachable,
}: {
  approachable: boolean;
}) => {
  tealiumAdapter.link('profile_update_succeeded', {
    ...profileState(),
    eventName: 'profile_update_succeeded',
    currentPageSection: 'visibility settings',
    updateType: 'edit',
    currentPageSubsection: 'approachability',
    profileApproachabilityStatus: approachable ? 'on' : 'off',
  });
};

export const trackSharedProfileVisibilityUpdated = ({
  profileSharedStatus,
}: {
  profileSharedStatus: AnalyticsSharedProfileStatus;
}) => {
  tealiumAdapter.link('profile_update_succeeded', {
    ...profileState(),
    eventName: 'profile_update_succeeded',
    currentPageSection: 'visibility settings',
    updateType: 'edit',
    currentPageSubsection: 'shared profile',
    profileSharedStatus,
  });
};

export const trackSharedProfileLinkCreated = () => {
  tealiumAdapter.link('profile_update_succeeded', {
    ...profileState(),
    eventName: 'profile_update_succeeded',
    currentPageSection: 'visibility settings',
    currentPageSubsection: 'shared profile',
    updateType: 'add',
    profileSharedStatus: 'on',
  });
};
